import { AsyncPipe, NgTemplateOutlet } from '@angular/common';
import { booleanAttribute, ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { outputFromObservable, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { RouterModule } from '@angular/router';
import { TranslocoModule } from '@jsverse/transloco';
import { WA_WINDOW } from '@ng-web-apis/common';
import { SubscribeIntentionEnum } from '@rma/billing-api';
import {
  SubscriptionProductService,
  SubscriptionUpgradeService,
} from '@rma/dashboard/billing/subscription-plans/public/api-subscription-features';
import { SubscribeIntentionService } from '@rma/dashboard/billing/subscription-plans/public/api-subscription-intent';
import { SubscriptionAccess, SubscriptionInterval, SubscriptionTiers } from '@rma/dashboard/generic/domain/to-be-sorted';
import { FeatureFlagService, TempFeatureFlags } from '@rma/dashboard/generic/util/feature-flags';
import { NavigationService } from '@rma/dashboard/generic/util/navigation';
import { REDIRECT_PARAMS, REDIRECT_URL, RedirectUrl } from '@rma/dashboard/generic/util/router-utils';
import { ExperienceCustomisationService } from '@rma/dashboard/layout/public/api-experience';
import { RmaIconComponent } from '@rma/generic/ui/icon';
import { HardNavigationService } from '@rma/generic/util/posthog';
import { combineLatest, debounceTime, of, startWith, switchMap, take } from 'rxjs';

interface CheckoutSubscriptionParams {
  tier?: SubscriptionTiers;
  interval?: SubscriptionInterval;
  couponCode?: string;
  [REDIRECT_URL]?: string;
  [REDIRECT_PARAMS]?: string | null;
}

@Component({
  selector: 'rma-upgrade-button',
  templateUrl: './upgrade-button.component.html',
  styleUrls: ['./upgrade-button.component.scss'],
  host: {
    '[class.fullWidth]': 'fullWidth()',
  },
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [AsyncPipe, MatButtonModule, NgTemplateOutlet, RmaIconComponent, RouterModule, TranslocoModule],
})
export class UpgradeButtonComponent {
  private readonly window = inject(WA_WINDOW);
  private readonly hardNavigation = inject(HardNavigationService);
  private readonly subscribeIntentionService = inject(SubscribeIntentionService);
  private readonly subscriptionProductService = inject(SubscriptionProductService);
  private readonly subscriptionUpgradeService = inject(SubscriptionUpgradeService);
  private readonly navigationService = inject(NavigationService);
  private readonly featureService = inject(FeatureFlagService);
  private readonly experienceCustomisationService = inject(ExperienceCustomisationService);

  private readonly redirectToPlans = toSignal(this.featureService.getFeature$(TempFeatureFlags.UpgradeButtonRedirectToPlans, false), {
    initialValue: false,
  });

  public readonly tier = input<SubscriptionTiers>();
  public readonly interval = input<SubscriptionInterval>();

  public readonly couponCode = input<string>();
  public readonly feature = input<SubscriptionAccess | SubscriptionAccess[]>();
  public readonly intention = input<SubscribeIntentionEnum>();
  public readonly subscriptionPrimerUrl = input<string | null>(null);
  public readonly overrideText = input<string | null>(null);
  public readonly theme = input<'default' | 'basic' | 'stroked' | 'inline'>('default');
  public readonly dataId = input<string | null>(null);
  public readonly overrideLock = input<boolean | null>(null);
  public readonly redirectTo = input<RedirectUrl>();
  public readonly fullWidth = input(false, { transform: booleanAttribute });

  public readonly needsUpgrade$ = combineLatest({
    tier: toObservable(this.tier).pipe(startWith(undefined)),
    feature: toObservable(this.feature).pipe(startWith(undefined)),
    override: toObservable(this.overrideLock).pipe(startWith(null)),
  }).pipe(
    switchMap(({ tier, feature, override }) =>
      override !== null ? of(override) : this.subscriptionUpgradeService.requiresUpgrade(tier, feature),
    ),
  );

  public readonly upgradeRequired = outputFromObservable(this.needsUpgrade$);

  public readonly customUpgradePrimer = toSignal(this.experienceCustomisationService.customUpgradePrimer$, { initialValue: null });
  public readonly trialPeriodDays$ = this.subscriptionProductService.availableTrialDays$;
  public readonly queryParams = computed(() => {
    const tier = this.tier();
    const interval = this.interval();

    const couponCode = this.couponCode();
    const redirectTo = this.redirectTo();

    const queryParams: CheckoutSubscriptionParams = {};

    if (tier) {
      queryParams.tier = tier;
    }

    if (interval) {
      queryParams.interval = interval;
    }

    if (couponCode) {
      queryParams.couponCode = couponCode;
    }

    if (redirectTo) {
      queryParams[REDIRECT_URL] = redirectTo[REDIRECT_URL];
      queryParams[REDIRECT_PARAMS] = redirectTo[REDIRECT_PARAMS] || null;
    }

    return queryParams;
  });

  public readonly checkoutUrl = computed(() => {
    if (this.redirectToPlans()) {
      return '/billing/plans';
    }

    const subscriptionPrimerUrl = this.subscriptionPrimerUrl();

    if (subscriptionPrimerUrl) {
      return subscriptionPrimerUrl;
    }

    return this.tier() ? '/billing/checkout' : '/billing/plans';
  });

  public onClick(): void {
    const url = this.window.location.pathname;
    if (url) {
      sessionStorage.setItem('upgraded-from', url);
    }
    const intention = this.intention();
    if (intention) {
      this.subscribeIntentionService
        .recordUserSubscribeIntention(intention)
        .pipe(debounceTime(500), take(1))
        .subscribe((_) => this.navigate());
    } else {
      this.navigate();
    }
  }

  public navigate(): void {
    const customUpgradePrimer = this.customUpgradePrimer();
    if (customUpgradePrimer) {
      this.hardNavigation.assign(customUpgradePrimer);
      return;
    }

    if (this.checkoutUrl()) {
      this.navigationService.navigateTo(this.checkoutUrl(), this.queryParams());
    }
  }
}
